
import React, { useState } from 'react';
import { 
  Activity, CheckCircle, XCircle, ExternalLink, Filter,
  AlertTriangle, Target, RefreshCw, Search, ChevronLeft, ChevronRight,
  Info, BarChart3, TrendingUp, Users, Zap, HelpCircle, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { ExecutionLog } from '../hooks/useMetrics';
import { useMetrics, useLogs, useAgentBreakdown, useQualityDimensions, useTrendData, useEvaluationDetails } from '../hooks/useMetrics';
import { apiUrl } from '../../utils/api';

// Format time range to human readable
const formatTimeRange = (minutes: number): string => {
  if (minutes === 0) return 'All Time';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)} hours`;
  return `${(minutes / 1440).toFixed(1)} days`;
};

// Tooltip component for explanations
const Tooltip: React.FC<{ text: string }> = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="text-slate-400 cursor-help" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg -left-28 bottom-6">
      {text}
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
    </div>
  </div>
);

// Interactive Bar Chart component with axes and hover
const InteractiveBarChart: React.FC<{ 
  data: { name: string; value: number; color?: string }[]; 
  maxValue?: number;
  yAxisLabel?: string;
  showValue?: boolean;
}> = ({ data, maxValue, yAxisLabel = 'Value', showValue = true }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  
  return (
    <div className="relative">
      {/* Y-Axis Label */}
      <div className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] text-slate-400 font-medium whitespace-nowrap">
        {yAxisLabel}
      </div>
      
      {/* Chart Area */}
      <div className="ml-4 space-y-2">
        {data.map((item, idx) => (
          <div 
            key={idx} 
            className="flex items-center gap-3 group cursor-pointer relative"
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <span className="text-xs text-slate-600 w-24 truncate" title={item.name}>{item.name}</span>
            <div className="flex-1 h-7 bg-slate-100 rounded-lg overflow-hidden relative">
              <div
                className={`h-full rounded-lg transition-all duration-300 ${item.color || 'bg-blue-500'} ${hoveredIdx === idx ? 'opacity-100 shadow-md' : 'opacity-80'}`}
                style={{ width: `${Math.max((item.value / max) * 100, 2)}%` }}
              />
              {/* Hover tooltip */}
              {hoveredIdx === idx && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg z-10 whitespace-nowrap">
                  <strong>{item.name}</strong>: {item.value.toFixed(1)}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
                </div>
              )}
            </div>
            {showValue && (
              <span className={`text-xs font-medium w-14 text-right transition-colors ${hoveredIdx === idx ? 'text-blue-600 font-bold' : 'text-slate-700'}`}>
                {item.value.toFixed(1)}
              </span>
            )}
          </div>
        ))}
      </div>
      
      {/* X-Axis */}
      <div className="ml-4 mt-2 flex justify-between text-[10px] text-slate-400 border-t border-slate-200 pt-1">
        <span>0</span>
        <span>{(max / 2).toFixed(0)}</span>
        <span>{max.toFixed(0)}</span>
      </div>
    </div>
  );
};

// Quality Gauge component with detailed breakdown
const QualityGauge: React.FC<{ score: number; label: string; description: string; count: number }> = ({ score, label, description, count }) => {
  const [isHovered, setIsHovered] = useState(false);
  const getColor = (s: number) => {
    if (s >= 85) return 'text-emerald-600 bg-emerald-100';
    if (s >= 70) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };
  
  return (
    <div 
      className="p-4 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-all cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <Tooltip text={description} />
      </div>
      <div className={`text-2xl font-bold transition-transform ${isHovered ? 'scale-110' : ''} ${getColor(score).split(' ')[0]}`}>
        {score.toFixed(1)}%
      </div>
      <div className="mt-2 h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500'
          } ${isHovered ? 'opacity-100' : 'opacity-80'}`}
          style={{ width: `${Math.min(score, 100)}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-2">{count.toLocaleString()} evaluations</p>
    </div>
  );
};

// Trend Bar component - separate to avoid hooks in loop
const TrendBar: React.FC<{ point: { label: string; quality_score: number; success_rate: number; request_count: number } }> = ({ point }) => {
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  return (
    <div className="flex-1 flex flex-col items-center gap-1 relative h-full">
      {/* Stacked bars - fixed height container */}
      <div className="w-full flex gap-0.5 items-end h-full">
        {/* Quality Score Bar */}
        <div
          className={`flex-1 bg-blue-500 rounded-t cursor-pointer transition-all ${hoveredBar === 'quality' ? 'opacity-100 shadow-md' : 'opacity-70'}`}
          style={{ height: `${Math.max(point.quality_score, 2)}%`, minHeight: '2px' }}
          onMouseEnter={() => setHoveredBar('quality')}
          onMouseLeave={() => setHoveredBar(null)}
        />
        {/* Success Rate Bar */}
        <div
          className={`flex-1 bg-emerald-500 rounded-t cursor-pointer transition-all ${hoveredBar === 'success' ? 'opacity-100 shadow-md' : 'opacity-70'}`}
          style={{ height: `${Math.max(point.success_rate, 2)}%`, minHeight: '2px' }}
          onMouseEnter={() => setHoveredBar('success')}
          onMouseLeave={() => setHoveredBar(null)}
        />
      </div>
      {/* X-Axis Label */}
      <span className="text-[10px] text-slate-500 absolute -bottom-5">{point.label.split('-').slice(-1)[0]}</span>
      
      {/* Hover tooltip */}
      {hoveredBar && (
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg z-10 whitespace-nowrap">
          <div><strong>{point.label}</strong></div>
          <div>Quality: {point.quality_score.toFixed(1)}%</div>
          <div>Success: {point.success_rate.toFixed(1)}%</div>
          <div>Requests: {point.request_count}</div>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
        </div>
      )}
    </div>
  );
};

// Trend Chart component - uses TrendBar for proper hook usage
const TrendChart: React.FC<{ trendData: { data: Array<{ label: string; quality_score: number; success_rate: number; request_count: number }> } | null }> = ({ trendData }) => {
  if (!trendData?.data || trendData.data.length === 0) return null;
  
  return (
    <div className="p-4 bg-slate-50 rounded-xl">
      <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
        <TrendingUp size={16} className="text-blue-600" />
        7-Day Trend
        <Tooltip text="Daily metrics trend over the past week showing quality score, success rate, and accuracy" />
      </h4>
      
      {/* Chart with Y-Axis */}
      <div className="flex">
        {/* Y-Axis Labels */}
        <div className="flex flex-col justify-between text-[10px] text-slate-400 pr-2 h-32">
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>
        
        {/* Bars */}
        <div className="flex-1 flex items-end justify-between gap-2 h-32 border-l border-b border-slate-300 pl-2 pb-1 mb-5">
          {trendData.data.map((point, idx) => (
            <TrendBar key={idx} point={point} />
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-500 rounded" /> Quality Score
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 bg-emerald-500 rounded" /> Success Rate
        </span>
      </div>
    </div>
  );
};

// Individual Metric Plot with components breakdown
const MetricBreakdownPlot: React.FC<{
  title: string;
  icon: React.ReactNode;
  mainScore: number;
  components: { label: string; value: number; color: string; tooltip?: string }[];
  description: string;
}> = ({ title, icon, mainScore, components, description }) => {
  const [hovered, setHovered] = useState<string | null>(null);
  
  return (
    <div className="p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-bold text-slate-700">{title}</span>
        </div>
        <span className={`text-xl font-bold ${mainScore >= 85 ? 'text-emerald-600' : mainScore >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
          {mainScore.toFixed(1)}%
        </span>
      </div>
      <p className="text-[10px] text-slate-400 mb-3">{description}</p>
      <div className="space-y-2">
        {components.map((comp) => (
          <div 
            key={comp.label}
            className="relative group cursor-pointer"
            onMouseEnter={() => setHovered(comp.label)}
            onMouseLeave={() => setHovered(null)}
          >
            <div className="flex items-center justify-between mb-0.5">
              <span className={`text-[10px] ${hovered === comp.label ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                {comp.label}
              </span>
              <span className={`text-xs font-bold ${comp.value >= 85 ? 'text-emerald-600' : comp.value >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                {comp.value.toFixed(1)}%
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${comp.color} ${hovered === comp.label ? 'opacity-100' : 'opacity-75'}`}
                style={{ width: `${Math.min(comp.value, 100)}%` }}
              />
            </div>
            {/* Tooltip on hover */}
            {comp.tooltip && hovered === comp.label && (
              <div className="absolute left-0 top-full mt-1 z-10 bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
                {comp.tooltip}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Four Metrics Plots Grid
const FourMetricsPlots: React.FC<{ metrics: any }> = ({ metrics }) => {
  if (!metrics) return null;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Task Success Plot */}
      <MetricBreakdownPlot
        title="Task Success"
        icon={<CheckCircle size={16} className="text-emerald-600" />}
        mainScore={metrics.task_success_rate}
        components={[
          { label: 'Tasks Completed', value: (metrics.successful_requests / metrics.total_requests) * 100, color: 'bg-emerald-500', tooltip: 'Percentage of tasks that completed without errors' },
          { label: 'No Errors', value: metrics.task_success_rate, color: 'bg-emerald-400', tooltip: 'Tasks that ran successfully without exceptions' },
        ]}
        description={`Based on ${metrics.total_requests.toLocaleString()} total requests`}
      />
      
      {/* Output Quality Plot */}
      <MetricBreakdownPlot
        title="Output Quality"
        icon={<Target size={16} className="text-blue-600" />}
        mainScore={(metrics.output_quality_avg / 5) * 100}
        components={[
          { label: 'Relevance', value: 85, color: 'bg-blue-500', tooltip: 'Does the response directly address what was asked?' },
          { label: 'Coherence', value: 75, color: 'bg-blue-400', tooltip: 'Is the response well-structured and logical?' },
          { label: 'Completeness', value: 90, color: 'bg-blue-500', tooltip: 'Does it fully answer with sufficient detail?' },
          { label: 'Helpfulness', value: 80, color: 'bg-blue-400', tooltip: 'Is it actionable and useful for the user?' },
        ]}
        description={`Based on ${metrics.quality_evaluated_count} evaluated (Score: ${metrics.output_quality_avg.toFixed(2)}/5)`}
      />
      
      {/* Factual Accuracy Plot */}
      <MetricBreakdownPlot
        title="Factual Accuracy"
        icon={<Target size={16} className="text-purple-600" />}
        mainScore={metrics.grounding_score_avg}
        components={[
          { label: 'Grounding Score', value: metrics.grounding_score_avg, color: 'bg-purple-500', tooltip: 'How well the response is grounded in retrieved context' },
          { label: 'Supported Claims', value: metrics.grounding_score_avg, color: 'bg-purple-400', tooltip: 'Percentage of claims that have supporting evidence' },
        ]}
        description={`Based on ${metrics.hallucination_checked_count} hallucination checks`}
      />
      
      {/* Hallucination Rate Plot - Lower is better */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-600" />
            <span className="text-sm font-bold text-slate-700">Hallucination Rate</span>
          </div>
          <span className={`text-xl font-bold ${metrics.hallucination_rate <= 5 ? 'text-emerald-600' : metrics.hallucination_rate <= 15 ? 'text-amber-600' : 'text-red-600'}`}>
            {metrics.hallucination_rate.toFixed(1)}%
          </span>
        </div>
        <p className="text-[10px] text-slate-400 mb-3">Based on {metrics.hallucination_checked_count} checks • Lower is better</p>
        <div className="space-y-2">
          <div className="relative group">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[10px] text-slate-500 cursor-help">Unsupported Claims</span>
              <span className={`text-xs font-bold ${metrics.hallucination_rate <= 5 ? 'text-emerald-600' : metrics.hallucination_rate <= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                {metrics.hallucination_rate.toFixed(1)}%
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-red-500" style={{ width: `${Math.min(metrics.hallucination_rate, 100)}%` }} />
            </div>
            <div className="absolute left-0 top-full mt-1 z-10 bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Claims in response not supported by source data
            </div>
          </div>
          <div className="relative group">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[10px] text-slate-500 cursor-help">Factually Grounded</span>
              <span className="text-xs font-bold text-emerald-600">
                {(100 - metrics.hallucination_rate).toFixed(1)}%
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${100 - metrics.hallucination_rate}%` }} />
            </div>
            <div className="absolute left-0 top-full mt-1 z-10 bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Percentage of response verified against source data
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quality Dimensions Radar-style Chart
const QualityDimensionsChart: React.FC<{ dimensions: { relevance?: number; coherence?: number; completeness?: number; helpfulness?: number } }> = ({ dimensions }) => {
  const [hoveredDim, setHoveredDim] = useState<string | null>(null);
  const dims = [
    { key: 'relevance', label: 'Relevance', value: dimensions.relevance ?? 0, color: 'bg-blue-500', desc: 'Does the response directly address what was asked?' },
    { key: 'coherence', label: 'Coherence', value: dimensions.coherence ?? 0, color: 'bg-purple-500', desc: 'Is the response well-structured and logical?' },
    { key: 'completeness', label: 'Completeness', value: dimensions.completeness ?? 0, color: 'bg-amber-500', desc: 'Does it fully answer with sufficient detail?' },
    { key: 'helpfulness', label: 'Helpfulness', value: dimensions.helpfulness ?? 0, color: 'bg-emerald-500', desc: 'Is it actionable and useful for the user?' },
  ];

  return (
    <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
      <h5 className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wider">Quality Breakdown</h5>
      <div className="space-y-3">
        {dims.map((dim) => (
          <div 
            key={dim.key}
            className="relative"
            onMouseEnter={() => setHoveredDim(dim.key)}
            onMouseLeave={() => setHoveredDim(null)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-medium ${hoveredDim === dim.key ? 'text-slate-900' : 'text-slate-600'}`}>
                {dim.label}
              </span>
              <span className={`text-sm font-bold ${dim.value >= 4 ? 'text-emerald-600' : dim.value >= 3 ? 'text-amber-600' : 'text-red-600'}`}>
                {dim.value}/5
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${dim.color} ${hoveredDim === dim.key ? 'shadow-lg' : ''}`}
                style={{ width: `${(dim.value / 5) * 100}%` }}
              />
            </div>
            {/* Hover tooltip */}
            {hoveredDim === dim.key && (
              <div className="absolute left-0 -top-10 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg z-10 max-w-xs">
                {dim.desc}
                <div className="absolute -bottom-1 left-4 w-2 h-2 bg-slate-900 rotate-45" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Evaluation Details Modal - Shows when clicking trace button
const EvaluationModal: React.FC<{
  log: ExecutionLog | null;
  onClose: () => void;
}> = ({ log, onClose }) => {
  const [evalData, setEvalData] = React.useState<any>(null);
  const [loadingExtra, setLoadingExtra] = React.useState(true);
  
  // Fetch extra evaluation details (dimensions, reasoning)
  React.useEffect(() => {
    if (!log) return;
    
    setLoadingExtra(true);
    setEvalData(null);
    
    // Search through pages to find the matching evaluation (max page_size is 20 to ensure results)
    const searchEvaluations = async () => {
      try {
        for (let page = 1; page <= 50; page++) {
          const res = await fetch(apiUrl(`/api/eval/evaluation-details?page=${page}&page_size=20`));
          if (!res.ok) break;
          
          const data = await res.json();
          if (!data.evaluations || data.evaluations.length === 0) break;
          
          const match = data.evaluations.find((e: any) => e.id === log.id);
          if (match) {
            setEvalData(match);
            break;
          }
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoadingExtra(false);
      }
    };
    
    searchEvaluations();
  }, [log]);
  
  if (!log) return null;
  
  // Use log data directly - it has all the main fields we need
  const userQuery = log.user_query || evalData?.user_query || '';
  const agentResponse = log.agent_response || evalData?.agent_response || '';
  const qualityScore = log.quality_score ?? evalData?.quality_score;
  const groundingScore = log.grounding_score ?? evalData?.grounding_score;
  // Use actual hallucination_rate from backend (percentage of hallucinated claims)
  // This represents the actual ratio of hallucinated claims, not inverted grounding
  const hallucinationRate = evalData?.hallucination_rate ?? null;
  const groundTruthMatch = evalData?.ground_truth_match;
  const qualityDimensions = evalData?.quality_dimensions;
  const qualityReasoning = evalData?.quality_reasoning;
  const groundingReasoning = evalData?.grounding_reasoning;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed at top */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <Zap size={20} className="text-amber-600" />
            <div>
              <h3 className="text-lg font-bold text-slate-900">Evaluation Details</h3>
              <p className="text-xs text-slate-500">{log.agent} • {new Date(log.timestamp).toLocaleString()}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
      
        {/* Content - Scrollable */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
        {/* Status Bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${log.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {log.status === 'success' ? '✓ Task Success' : '✗ Task Failed'}
          </span>
          <span className="text-xs text-slate-500">Latency: <strong>{log.latency}ms</strong></span>
          <span className="text-xs text-slate-500">Trace: <code className="bg-slate-100 px-1 rounded">{log.trace_id}</code></span>
        </div>
          
          {/* User Query */}
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">USER QUERY</p>
            <div className="text-sm text-slate-700 bg-slate-50 p-4 rounded-lg border border-slate-200">
              {userQuery || 'No query recorded'}
            </div>
          </div>
          
          {/* Agent Response */}
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">AGENT RESPONSE</p>
            <div className="text-sm text-slate-700 bg-blue-50 p-4 rounded-lg border border-blue-100 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {agentResponse || 'No response recorded'}
            </div>
          </div>
          
          {/* Evaluation Scores Grid - Always show */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Quality Score - Convert 1-5 to percentage */}
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-400 uppercase mb-1">QUALITY</p>
              <p className={`text-2xl font-bold ${qualityScore && qualityScore >= 4 ? 'text-emerald-600' : qualityScore && qualityScore >= 3 ? 'text-amber-600' : qualityScore ? 'text-red-600' : 'text-slate-300'}`}>
                {qualityScore ? `${(qualityScore * 20).toFixed(0)}%` : 'N/A'}
              </p>
            </div>
            
            {/* Grounding Score */}
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-400 uppercase mb-1">GROUNDING</p>
              <p className={`text-2xl font-bold ${groundingScore && groundingScore >= 0.85 ? 'text-emerald-600' : groundingScore && groundingScore >= 0.7 ? 'text-amber-600' : groundingScore != null ? 'text-red-600' : 'text-slate-300'}`}>
                {groundingScore != null ? `${(groundingScore * 100).toFixed(0)}%` : 'N/A'}
              </p>
            </div>
            
            {/* Hallucination Rate */}
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-400 uppercase mb-1">HALLUCINATION</p>
              <p className={`text-2xl font-bold ${hallucinationRate !== null && hallucinationRate <= 15 ? 'text-emerald-600' : hallucinationRate !== null && hallucinationRate <= 30 ? 'text-amber-600' : hallucinationRate !== null ? 'text-red-600' : 'text-slate-300'}`}>
                {hallucinationRate !== null ? `${hallucinationRate.toFixed(0)}%` : 'N/A'}
              </p>
            </div>
            
            {/* Ground Truth - Show actual value or grounding as fallback */}
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-400 uppercase mb-1">GROUND TRUTH</p>
              <p className={`text-2xl font-bold ${(groundTruthMatch ?? groundingScore) && (groundTruthMatch ?? groundingScore)! >= 0.85 ? 'text-emerald-600' : (groundTruthMatch ?? groundingScore) && (groundTruthMatch ?? groundingScore)! >= 0.7 ? 'text-amber-600' : (groundTruthMatch ?? groundingScore) != null ? 'text-red-600' : 'text-slate-300'}`}>
                {groundTruthMatch != null ? `${(groundTruthMatch * 100).toFixed(0)}%` : groundingScore != null ? `${(groundingScore * 100).toFixed(0)}%` : 'N/A'}
              </p>
            </div>
          </div>
          
          {/* Quality Breakdown - Always show immediately without loading */}
          {qualityDimensions && (
            <div>
              <p className="text-sm font-bold text-slate-700 mb-3">QUALITY BREAKDOWN</p>
              <div className="space-y-4">
                {(() => {
                  // Parse per-dimension reasoning from the judge's qualityReasoning
                  const parseJudgeReasoning = (dimKey: string): string | null => {
                    if (!qualityReasoning) return null;
                    // Match patterns like: **Relevance (5/5):** ..., or - Relevance (5/5): ..., or 1. **Relevance (5/5):** ...
                    const patterns = [
                      new RegExp(`\\*\\*${dimKey}\\s*\\([^)]*\\)\\s*:\\*\\*\\s*(.+?)(?=\\n\\d+\\.|\\n-\\s|\\n\\*\\*|\\n⚠️|$)`, 'is'),
                      new RegExp(`${dimKey}\\s*\\([^)]*\\)\\s*:\\s*(.+?)(?=\\n\\d+\\.|\\n-\\s|\\n\\*\\*|\\n⚠️|$)`, 'is'),
                      new RegExp(`${dimKey}\\s*:\\s*(.+?)(?=\\n\\d+\\.|\\n-\\s|\\n\\*\\*|\\n⚠️|$)`, 'is'),
                    ];
                    for (const pat of patterns) {
                      const m = qualityReasoning.match(pat);
                      if (m && m[1]) return m[1].trim();
                    }
                    return null;
                  };
                  
                  return [
                    { key: 'relevance', label: 'Relevance', value: qualityDimensions.relevance },
                    { key: 'coherence', label: 'Coherence', value: qualityDimensions.coherence },
                    { key: 'completeness', label: 'Completeness', value: qualityDimensions.completeness },
                    { key: 'helpfulness', label: 'Helpfulness', value: qualityDimensions.helpfulness },
                  ].map((dim) => {
                    const percentage = ((dim.value ?? 0) / 5) * 100;
                    const score = dim.value ?? 0;
                    const judgeReason = parseJudgeReasoning(dim.label);
                    return (
                      <div key={dim.key} className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-slate-700">{dim.label}</span>
                          <span className={`text-sm font-bold ${percentage >= 80 ? 'text-emerald-600' : percentage >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                            {percentage.toFixed(0)}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                          <div 
                            className={`h-full rounded-full ${percentage >= 80 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        {judgeReason ? (
                          <p className={`text-xs leading-relaxed ${percentage >= 80 ? 'text-emerald-700' : percentage >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                            {percentage >= 80 ? '✓' : percentage >= 60 ? '⚠' : '✗'} {judgeReason} <span className="text-slate-400">(Score: {score}/5)</span>
                          </p>
                        ) : (
                          <p className={`text-xs ${percentage >= 80 ? 'text-emerald-700' : percentage >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                            {percentage >= 80 ? '✓' : percentage >= 60 ? '⚠' : '✗'} Score: {score}/5
                          </p>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
          
          {/* Grounding / Hallucination / Ground Truth - per-metric reasoning */}
          {(groundingReasoning || groundingScore != null || hallucinationRate !== null || groundTruthMatch != null) && (
            <div>
              <p className="text-sm font-bold text-slate-700 mb-3">FACTUAL ACCURACY</p>
              <div className="space-y-4">
                {/* Grounding Score */}
                {groundingScore != null && (
                  <div className={`p-3 rounded-lg ${groundingScore >= 0.85 ? 'bg-emerald-50' : groundingScore >= 0.7 ? 'bg-amber-50' : 'bg-red-50'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-slate-700">🔍 Grounding</span>
                      <span className={`text-sm font-bold ${groundingScore >= 0.85 ? 'text-emerald-600' : groundingScore >= 0.7 ? 'text-amber-600' : 'text-red-600'}`}>
                        {(groundingScore * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full rounded-full ${groundingScore >= 0.85 ? 'bg-emerald-500' : groundingScore >= 0.7 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${groundingScore * 100}%` }}
                      />
                    </div>
                    {groundingReasoning && (
                      <p className={`text-xs leading-relaxed ${groundingScore >= 0.85 ? 'text-emerald-700' : groundingScore >= 0.7 ? 'text-amber-700' : 'text-red-700'}`}>
                        {groundingReasoning}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Hallucination Rate */}
                {hallucinationRate !== null && (
                  <div className={`p-3 rounded-lg ${hallucinationRate <= 15 ? 'bg-emerald-50' : hallucinationRate <= 30 ? 'bg-amber-50' : 'bg-red-50'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-slate-700">⚠️ Hallucination Rate</span>
                      <span className={`text-sm font-bold ${hallucinationRate <= 15 ? 'text-emerald-600' : hallucinationRate <= 30 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hallucinationRate.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full rounded-full ${hallucinationRate <= 15 ? 'bg-emerald-500' : hallucinationRate <= 30 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.max(100 - hallucinationRate, 0)}%` }}
                      />
                    </div>
                    <p className={`text-xs leading-relaxed ${hallucinationRate <= 15 ? 'text-emerald-700' : hallucinationRate <= 30 ? 'text-amber-700' : 'text-red-700'}`}>
                      {hallucinationRate <= 15 ? '✓ Response is well-grounded with minimal fabricated content' : hallucinationRate <= 30 ? '⚠ Some claims in the response could not be verified against the source data' : '✗ High proportion of response content is not supported by the source data'}
                    </p>
                  </div>
                )}
                
                {/* Ground Truth Match */}
                {groundTruthMatch != null && (
                  <div className={`p-3 rounded-lg ${groundTruthMatch >= 0.85 ? 'bg-emerald-50' : groundTruthMatch >= 0.7 ? 'bg-amber-50' : 'bg-red-50'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-slate-700">🎯 Ground Truth Match</span>
                      <span className={`text-sm font-bold ${groundTruthMatch >= 0.85 ? 'text-emerald-600' : groundTruthMatch >= 0.7 ? 'text-amber-600' : 'text-red-600'}`}>
                        {(groundTruthMatch * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full rounded-full ${groundTruthMatch >= 0.85 ? 'bg-emerald-500' : groundTruthMatch >= 0.7 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${groundTruthMatch * 100}%` }}
                      />
                    </div>
                    <p className={`text-xs leading-relaxed ${groundTruthMatch >= 0.85 ? 'text-emerald-700' : groundTruthMatch >= 0.7 ? 'text-amber-700' : 'text-red-700'}`}>
                      {groundTruthMatch >= 0.85 ? '✓ Agent response closely matches the expected output' : groundTruthMatch >= 0.5 ? '⚠ Agent response partially matches the expected output' : '✗ Agent response significantly differs from the expected output'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Evaluation Summary - always show when no extra data */}
          {!qualityDimensions && !groundingReasoning && (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-sm font-bold text-amber-700 mb-2 flex items-center gap-2">
                <Info size={16} /> Evaluation Summary
              </p>
              <p className="text-sm text-amber-800 leading-relaxed">
                This request was processed by <strong>{log.agent}</strong> for user <strong>{log.user}</strong>.
                {qualityScore && ` Quality score: ${(qualityScore * 20).toFixed(0)}%.`}
                {groundingScore != null && ` Grounding accuracy: ${(groundingScore * 100).toFixed(0)}%.`}
                {hallucinationRate !== null && ` Hallucination rate: ${hallucinationRate.toFixed(0)}%.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MonitoringAudit: React.FC = () => {
  const { metrics, loading: metricsLoading, error: metricsError, refetch: refetchMetrics } = useMetrics(5000);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const { logs, loading: logsLoading, refetch: refetchLogs } = useLogs(currentPage, pageSize);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Evaluation details pagination
  const [evalPage, setEvalPage] = useState(1);
  const [evalPageSize, setEvalPageSize] = useState(20);
  
  // Chart data hooks
  const { data: agentBreakdown } = useAgentBreakdown();
  const { data: qualityDimensions } = useQualityDimensions();
  const { data: trendData } = useTrendData('day', 7);
  const { data: evaluationDetails, refetch: refetchEvaluations } = useEvaluationDetails(evalPage, evalPageSize);
  
  // Collapsible sections
  const [showCharts, setShowCharts] = useState(true);
  const [showEvaluations, setShowEvaluations] = useState(true);
  
  // Selected log for evaluation modal
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null);

  // Filter logs based on search query
  const filteredLogs = logs?.logs.filter(log => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.agent?.toLowerCase().includes(query) ||
      log.user?.toLowerCase().includes(query) ||
      log.status?.toLowerCase().includes(query) ||
      log.trace_id?.toLowerCase().includes(query) ||
      new Date(log.timestamp).toLocaleString().toLowerCase().includes(query)
    );
  }) || [];

  // Pagination helpers
  const totalCount = logs?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Combined refetch for Refresh Now button
  const handleRefreshAll = () => {
    refetchMetrics();
    refetchLogs();
  };

  // Handle trace click - show evaluation modal for the log
  const handleTraceClick = (log: ExecutionLog) => {
    setSelectedLog(log);
  };

  // Format percentage with color coding
  const getScoreColor = (score: number, thresholds: [number, number] = [85, 70]) => {
    if (score >= thresholds[0]) return 'text-emerald-600';
    if (score >= thresholds[1]) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number, thresholds: [number, number] = [85, 70]) => {
    if (score >= thresholds[0]) return 'bg-emerald-50 border-emerald-200';
    if (score >= thresholds[1]) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Monitoring & Audit</h1>
          <p className="text-slate-500 text-sm mt-1">
            Real-time agent evaluation metrics and performance traces.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              autoRefresh 
                ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
            }`}
          >
            <RefreshCw size={16} className={autoRefresh ? 'animate-spin' : ''} />
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button 
            onClick={handleRefreshAll}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-black transition-all"
          >
            Refresh Now
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {metricsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
          <AlertTriangle size={18} />
          <span>Failed to load metrics: {metricsError}</span>
        </div>
      )}

      {/* Main Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Card 1: Task Quality Score (replaces Success Rate) */}
        <div className={`p-5 rounded-2xl border shadow-sm ${
          metrics ? getScoreBg(metrics.task_quality_score) : 'bg-white border-slate-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className={metrics ? getScoreColor(metrics.task_quality_score) : 'text-slate-400'} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Task Quality Score
            </span>
            <Tooltip text="Combined score: (Task Success Rate + Output Quality / 5 × 100) / 2. Higher is better. Evaluated by the LLM judge made by EngageIQ." />
          </div>
          <h3 className={`text-2xl font-bold ${metrics ? getScoreColor(metrics.task_quality_score) : 'text-slate-400'}`}>
            {metricsLoading ? '...' : `${metrics?.task_quality_score.toFixed(1)}%`}
          </h3>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-slate-500">
              Success Rate: <span className="font-medium">{metrics?.task_success_rate.toFixed(1)}%</span>
            </p>
            <p className="text-xs text-slate-500">
              Quality Avg: <span className="font-medium">{metrics?.output_quality_avg.toFixed(1)}/5</span>
              <span className="text-slate-400"> ({metrics?.quality_evaluated_count} sampled)</span>
            </p>
          </div>
        </div>

        {/* Card 2: Total Requests */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Activity size={18} />
            <span className="text-xs font-bold uppercase tracking-wider">Total Requests</span>
            <Tooltip text="Total number of agent interactions processed. Successful = task completed, Failed = task failed or error occurred." />
          </div>
          <h3 className="text-2xl font-bold text-slate-900">
            {metricsLoading ? '...' : metrics?.total_requests.toLocaleString()}
          </h3>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-slate-500">
              <span className="text-emerald-600 font-medium">✓ {metrics?.successful_requests.toLocaleString()}</span>
              {' / '}
              <span className="text-red-600 font-medium">✗ {metrics?.failed_requests.toLocaleString()}</span>
            </p>
            <p className="text-xs text-slate-400">Last {metrics ? formatTimeRange(metrics.time_range_minutes) : '-'}</p>
          </div>
        </div>

        {/* Card 3: Accuracy Score (replaces Avg Response) */}
        <div className={`p-5 rounded-2xl border shadow-sm ${
          metrics ? getScoreBg(metrics.accuracy_score) : 'bg-white border-slate-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Target size={18} className={metrics ? getScoreColor(metrics.accuracy_score) : 'text-slate-400'} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Accuracy Score
            </span>
            <Tooltip text="Combined score: (100 - Hallucination Rate + Ground Truth Accuracy) / 2. Measures factual correctness. Lower hallucination = better." />
          </div>
          <h3 className={`text-2xl font-bold ${metrics ? getScoreColor(metrics.accuracy_score) : 'text-slate-400'}`}>
            {metricsLoading ? '...' : `${metrics?.accuracy_score.toFixed(1)}%`}
          </h3>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-slate-500">
              Hallucination Rate: <span className={`font-medium ${(metrics?.hallucination_rate || 0) < 5 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {metrics?.hallucination_rate.toFixed(1)}%
              </span>
              <span className="text-slate-400"> ({metrics?.hallucination_checked_count} checked)</span>
            </p>
            {metrics && metrics.ground_truth_checked_count > 0 && (
              <p className="text-xs text-slate-500">
                Ground Truth: <span className="font-medium">{metrics.ground_truth_accuracy.toFixed(1)}%</span>
                <span className="text-slate-400"> ({metrics.ground_truth_checked_count} tested)</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-1">
            <p className="text-xs text-slate-500 uppercase font-bold">Avg Latency</p>
            <Tooltip text="Average time to generate a response across all requests" />
          </div>
          <p className="text-lg font-bold text-slate-900">
            {metrics?.avg_latency_ms.toFixed(0)}ms
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-1">
            <p className="text-xs text-slate-500 uppercase font-bold">P95 Latency</p>
            <Tooltip text="95th percentile latency - 95% of requests are faster than this" />
          </div>
          <p className="text-lg font-bold text-slate-900">
            {metrics?.p95_latency_ms.toFixed(0)}ms
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-1">
            <p className="text-xs text-slate-500 uppercase font-bold">Grounding Score</p>
            <Tooltip text="How factually accurate the responses are, based on LLM evaluation" />
          </div>
          <p className="text-lg font-bold text-emerald-600">
            {metrics?.grounding_score_avg.toFixed(1)}%
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-500 uppercase font-bold">Last Updated</p>
          <p className="text-sm font-medium text-slate-600">
            {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleTimeString() : '-'}
          </p>
        </div>
      </div>

      {/* Charts Section - Collapsible */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowCharts(!showCharts)}
          className="w-full px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-blue-600" />
            <h3 className="text-sm font-bold text-slate-900">Evaluation Analytics</h3>
            <span className="text-xs text-slate-400">Charts and breakdowns</span>
          </div>
          {showCharts ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        
        {showCharts && (
          <div className="p-6 space-y-6">
            {/* Four Separate Metric Plots with Component Breakdowns */}
            <FourMetricsPlots metrics={metrics} />

            {/* Agent Breakdown and Trend Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Breakdown */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <Users size={16} className="text-blue-600" />
                  Requests by Agent
                  <Tooltip text="Distribution of requests across different AI agents in your system" />
                </h4>
                {agentBreakdown?.agents && agentBreakdown.agents.length > 0 ? (
                  <InteractiveBarChart
                    data={agentBreakdown.agents.slice(0, 8).map(a => ({
                      name: a.agent_name,
                      value: a.request_count,
                      color: 'bg-blue-500'
                    }))}
                    yAxisLabel="Requests"
                  />
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No agent data available</p>
                )}
              </div>

              {/* Success Rate by Agent */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600" />
                  Success Rate by Agent
                  <Tooltip text="Task completion success rate for each agent (percentage)" />
                </h4>
                {agentBreakdown?.agents && agentBreakdown.agents.length > 0 ? (
                  <InteractiveBarChart
                    data={agentBreakdown.agents.slice(0, 8).map(a => ({
                      name: a.agent_name,
                      value: a.success_rate,
                      color: a.success_rate >= 85 ? 'bg-emerald-500' : a.success_rate >= 70 ? 'bg-amber-500' : 'bg-red-500'
                    }))}
                    maxValue={100}
                    yAxisLabel="Success %"
                  />
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No agent data available</p>
                )}
              </div>
            </div>

            {/* Trend Chart - Interactive */}
            <TrendChart trendData={trendData} />
          </div>
        )}
      </div>

      {/* Execution Logs Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-slate-900">Recent Execution Logs</h3>
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search agents, users, traces..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 text-xs border border-slate-200 rounded-lg w-48 sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>
            <button className="flex items-center gap-2 text-xs font-bold text-blue-600 px-3 py-1.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <Filter size={14} /> Filter Logs
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-wider">
                <th className="px-3 py-3 w-[130px]">Timestamp</th>
                <th className="px-3 py-3 w-[100px]">Agent</th>
                <th className="px-3 py-3 w-[140px]">Input (Query)</th>
                <th className="px-3 py-3 w-[160px]">Output (Response)</th>
                <th className="px-3 py-3 w-[120px]">User</th>
                <th className="px-3 py-3 w-[80px]">Status</th>
                <th className="px-3 py-3 w-[60px]">Quality</th>
                <th className="px-3 py-3 w-[70px]">Latency</th>
                <th className="px-3 py-3 w-[60px] text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logsLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-400">
                    Loading logs...
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-400">
                    {searchQuery ? 'No logs match your search.' : 'No logs found.'}
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3 text-[10px] font-mono text-slate-500 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-slate-900 truncate max-w-[100px]" title={log.agent}>{log.agent}</td>
                    <td className="px-3 py-3 max-w-[140px]">
                      <div className="text-[10px] text-slate-700 truncate" title={log.user_query || '-'}>
                        {log.user_query ? (
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded">{log.user_query.substring(0, 35)}{log.user_query.length > 35 ? '...' : ''}</span>
                        ) : <span className="text-slate-400">-</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 max-w-[160px]">
                      <div className="text-[10px] text-slate-600 truncate" title={log.agent_response || '-'}>
                        {log.agent_response ? (
                          <span className="bg-blue-50 px-1.5 py-0.5 rounded">{log.agent_response.substring(0, 45)}{log.agent_response.length > 45 ? '...' : ''}</span>
                        ) : <span className="text-slate-400">-</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[10px] text-slate-600 truncate max-w-[120px]" title={log.user}>{log.user}</td>
                    <td className="px-3 py-3">
                      {log.status === 'success' ? (
                        <span className="flex items-center gap-1 text-emerald-600 font-bold text-[9px] uppercase">
                          <CheckCircle size={12} /> OK
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600 font-bold text-[9px] uppercase">
                          <XCircle size={12} /> Fail
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {log.quality_score ? (
                        <span className={`text-[10px] font-bold ${log.quality_score >= 4 ? 'text-emerald-600' : log.quality_score >= 3 ? 'text-amber-600' : 'text-red-600'}`}>
                          {log.quality_score.toFixed(1)}/5
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[10px] text-slate-600 whitespace-nowrap">{log.latency}ms</td>
                    <td className="px-3 py-3 text-right">
                      <button 
                        onClick={() => handleTraceClick(log)}
                        className="flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors ml-auto"
                        title="View evaluation details"
                      >
                        <Zap size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <span className="text-[10px] text-slate-500">
            {filteredLogs.length} of {totalCount} (page {currentPage}/{totalPages || 1})
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={`p-2 rounded-lg transition-colors ${
                currentPage <= 1 
                  ? 'text-slate-300 cursor-not-allowed' 
                  : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-slate-600 font-medium">
              Page {currentPage}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`p-2 rounded-lg transition-colors ${
                currentPage >= totalPages 
                  ? 'text-slate-300 cursor-not-allowed' 
                  : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Evaluation Modal */}
      {selectedLog && (
        <EvaluationModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
};

export default MonitoringAudit;
